'use server'

import { apiRequest, ApiResponse } from '../request'

export type CleaningStatus =
    | 'CLEAN_REQUESTED'
    | 'CLEAN_IN_PROGRESS'
    | 'CLEAN_COMPLETED'
    | 'CLEAN_VERIFIED'

export type CleaningType = 'TERMINAL' | 'CONCURRENT' | 'ROUTINE'

export interface BedCleaning {
    id: number
    companyId: number
    hospitalId: number
    bedId: number
    bed?: {
        id: number
        number: string
        unit?: {
            id: number
            name: string
        }
    }
    type: CleaningType
    status: CleaningStatus
    requestedAt: string
    requestedById?: number
    requestedBy?: {
        id: number
        name: string
    }
    startedAt?: string
    startedById?: number
    startedBy?: {
        id: number
        name: string
    }
    completedAt?: string
    completedById?: number
    completedBy?: {
        id: number
        name: string
    }
    verifiedAt?: string
    verifiedById?: number
    verifiedBy?: {
        id: number
        name: string
    }
    notes?: string
    createdAt: string
    updatedAt: string
}

export interface RequestCleaningData {
    bedId: number
    type: CleaningType
    notes?: string
}

export interface UpdateCleaningData {
    status: CleaningStatus
    notes?: string
}

export interface CleaningFilters {
    hospitalId?: number
    status?: CleaningStatus
    type?: CleaningType
    unitId?: number
}

export async function getCleanings(
    companyId?: number,
    filters?: CleaningFilters
): Promise<ApiResponse<BedCleaning[]>> {
    return apiRequest<BedCleaning[]>('hospital/cleaning', {
        params: { companyId, ...filters },
    })
}

export async function getCleaning(id: number): Promise<ApiResponse<BedCleaning>> {
    return apiRequest<BedCleaning>(`hospital/cleaning/${id}`, {})
}

export async function getPendingCleanings(
    companyId: number,
    hospitalId?: number,
): Promise<ApiResponse<BedCleaning[]>> {
    return apiRequest<BedCleaning[]>(`hospital/cleaning/pending/${companyId}`, {
        params: { hospitalId },
    })
}

export async function requestCleaning(
    data: RequestCleaningData
): Promise<ApiResponse<BedCleaning>> {
    return apiRequest<BedCleaning>('hospital/cleaning', { data }, 'post')
}

export async function startCleaning(
    id: number
): Promise<ApiResponse<BedCleaning>> {
    return apiRequest<BedCleaning>(`hospital/cleaning/${id}/start`, {}, 'patch')
}

export async function completeCleaning(
    id: number,
    notes?: string
): Promise<ApiResponse<BedCleaning>> {
    return apiRequest<BedCleaning>(
        `hospital/cleaning/${id}/complete`,
        { data: { notes } },
        'patch'
    )
}

export async function verifyCleaning(
    id: number
): Promise<ApiResponse<BedCleaning>> {
    return apiRequest<BedCleaning>(`hospital/cleaning/${id}/verify`, {}, 'patch')
}

export const CLEANING_STATUS_LABELS: Record<CleaningStatus, string> = {
    CLEAN_REQUESTED: 'Solicitada',
    CLEAN_IN_PROGRESS: 'Em Andamento',
    CLEAN_COMPLETED: 'Concluída',
    CLEAN_VERIFIED: 'Verificada',
}

export const CLEANING_STATUS_COLORS: Record<CleaningStatus, string> = {
    CLEAN_REQUESTED: 'badge-warning',
    CLEAN_IN_PROGRESS: 'badge-info',
    CLEAN_COMPLETED: 'badge-success',
    CLEAN_VERIFIED: 'badge-secondary',
}

export const CLEANING_TYPE_LABELS: Record<CleaningType, string> = {
    TERMINAL: 'Terminal',
    CONCURRENT: 'Concorrente',
    ROUTINE: 'Rotina',
}
